<template>
  <PuzzleListView
    :items="sightEntity.sights"
    :more-button="sightEntity.hasNext"
    :more-button-text="props.pagination ? t('viewMore') : t('seeAll')"
    :more-button-link="props.pagination ? '' : allSightsLink"
    :loading="loading"
    :number-of-skeleton-cards="5"
    @load-more="showAllSights"
    @item-click="goToSight"
  >
    <template #default="{ item, position }">
      <PuzzleListArticleCard
        v-view-tracker="getParams(item)"
        :item="{ ...item, link: `/journal/articles/${item.slug}/` }"
        :position="position"
      />
    </template>

    <template #skeleton>
      <PuzzleListArticleCardSkeleton :loading="skeletonLoading" />
    </template>
  </PuzzleListView>
</template>

<script lang="ts" setup>
  import { useAppRouter } from '@experience/core-app-link-lib';
  import { useI18n } from '@experience/i18n';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import { useRoute } from 'vue-router';
  import { trackEvent, useViewTracker } from '@/services/analytics';
  import PuzzleListView from '@/features/PuzzleListView.vue';
  import { useSightEntity } from '@/entities/sight';
  import type { ArticleInList } from '@/shared/models/articleInList';
  import { PuzzleListArticleCard, PuzzleListArticleCardSkeleton } from '@/shared/ui/puzzleListArticleCard';
  import { ru } from '../i18n/ru';

  const vViewTracker = useViewTracker('articles-sights-info:sights.view');

  const getParams = (item: ArticleInList) => ({
    article_id: item.id,
  });

  interface Props {
    /**
     * Ссылка для открытия страницы со списком всех достопримечательностей.
     */
    allSightsLink?: string;

    /**
     * Идентификатор города.
     */
    subjectGeoId: number;

    /**
     * Название города.
     */
    subjectGeoName: string;

    /**
     * Флаг загрузки сторонних данных.
     *
     * Данный флаг нужен для того, чтобы не отсылать события аналитики до того
     * как загрузятся все данные, отправляемые в событиях.
     */
    generalPageDataLoading: boolean;

    /**
     * Пагинация.
     */
    pagination: boolean;
  }

  const props = defineProps<Props>();

  const route = useRoute();
  const slug = computed(() => String(route.params.slug));

  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const { routerProxy } = useAppRouter();

  const sightEntity = useSightEntity();
  const { loading: sightLoading, loadingFailed } = storeToRefs(sightEntity);

  const loading = computed(() => sightLoading.value || props.generalPageDataLoading);
  const skeletonLoading = computed(() => loading.value && !loadingFailed.value);

  const sightsEntity = useSightEntity();

  const showAllSights = async () => {
    trackEvent('articles-sights-info:show-more-button.click', {} as never);

    if (props.allSightsLink) {
      await routerProxy.push(props.allSightsLink);
    }

    if (props.pagination) {
      const citySlugs = [slug.value];
      sightsEntity.getSights({ pageSize: 20, citySlugs });
    }
  };

  const goToSight = (sight: ArticleInList) => {
    trackEvent('articles-sights-info:sights.click', getParams(sight));

    window.open(`/journal/articles/${sight.slug}/`, '_blank');
  };
</script>
